/**
 * Переговоры: список, карточка, мандат, решения (BE-177, FE-170…FE-174).
 *
 * Три правила слоя, те же что у четвёрки:
 *
 * * **необратимые действия без оптимистичного UI** (CLAUDE.md §8). Согласие с
 *   предложением и остановка переговоров уходят письмом автохаусу — рисовать
 *   результат до ответа сервера нельзя;
 * * **ключ идемпотентности живёт до успеха, а не до рендера**: повтор после
 *   обрыва сети обязан попасть в то же действие, а не отправить второе письмо;
 * * **ответ мутации кладётся в кэш**: он и есть новое состояние карточки.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { apiFetch } from '@shared/api/client';
import { queryKeys } from '@shared/api/queryKeys';
import { randomUuid } from '@shared/lib/uuid';

import type {
  MandateInput,
  NegotiationDetail,
  NegotiationList,
  NegotiationPreview,
  NegotiationStatus,
  SellerMetrics,
} from './types';

/**
 * Пока ход за автохаусом, карточка обновляется сама.
 *
 * Тридцать секунд — не «реальное время»: ответ автохауса приходит письмом и
 * разбирается воркером по расписанию опроса ящика. Чаще опрашивать нечего.
 */
const THREAD_POLL_MS = 30_000;

/**
 * Пока письмо стоит в окне отмены, карточка опрашивается каждые 2 секунды
 * (FE-180): баннер с таймером обязан заметить отправку и отмену без
 * перезагрузки — иначе кнопка «Отменить» жила бы дольше самого окна.
 */
const SCHEDULED_POLL_MS = 2_000;

export function useNegotiations(statuses?: NegotiationStatus[]) {
  const query = statuses && statuses.length > 0 ? { status: statuses } : {};
  const search = new URLSearchParams();
  for (const status of statuses ?? []) search.append('status', status);
  const suffix = search.size > 0 ? `?${search.toString()}` : '';

  return useQuery({
    queryKey: queryKeys.negotiations(query),
    queryFn: () => apiFetch<NegotiationList>(`/negotiations${suffix}`),
    retry: false,
  });
}

export function useNegotiation(negotiationId: string | null) {
  return useQuery({
    queryKey: queryKeys.negotiation(negotiationId ?? ''),
    queryFn: () => apiFetch<NegotiationDetail>(`/negotiations/${negotiationId ?? ''}`),
    enabled: negotiationId !== null && negotiationId !== '',
    retry: false,
    refetchInterval: (query) => {
      const detail = query.state.data;
      if (detail === undefined) return false;
      if (detail.messages.some((message) => message.cancellable)) return SCHEDULED_POLL_MS;
      return detail.next_actor === 'dealer' ||
        detail.next_actor === 'agent' ||
        detail.next_actor === 'agent_processing'
        ? THREAD_POLL_MS
        : false;
    },
  });
}

/**
 * Профиль коммуникации автохауса (FE-240, MVP_PRD §37).
 *
 * Отдельный запрос, а не поле карточки: профиль пересчитывается на сервере при
 * чтении, и тащить его в каждый опрос ленты писем (раз в 2 секунды в окне
 * отмены) незачем. `null` в ответе — «Нет данных»: автохаусу ещё не писали.
 *
 * Обновляется вместе с карточкой: новый ответ автохауса меняет и полноту, и
 * скорость, и оба числа обязаны совпадать с тем, что видно в таймлайне.
 */
export function useNegotiationMetrics(negotiationId: string | null) {
  return useQuery({
    queryKey: queryKeys.negotiationMetrics(negotiationId ?? ''),
    queryFn: () => apiFetch<SellerMetrics | null>(`/negotiations/${negotiationId ?? ''}/metrics`),
    enabled: negotiationId !== null && negotiationId !== '',
    retry: false,
  });
}

/**
 * Предпросмотр запуска: предзаполнение мандата и письмо-образец (BE-172).
 *
 * Ответ **не кэшируется надолго**: предусловия запуска меняются извне —
 * объявление снимают, продавец отказывается от переписки, слот занимает
 * другая переписка.
 */
export function useNegotiationPreview(vehicleId: string | null) {
  return useQuery({
    queryKey: queryKeys.negotiationPreview(vehicleId ?? ''),
    queryFn: () =>
      apiFetch<NegotiationPreview>(
        `/vehicles/${encodeURIComponent(vehicleId ?? '')}/negotiation-preview`,
      ),
    enabled: vehicleId !== null && vehicleId !== '',
    retry: false,
    staleTime: 0,
  });
}

function useApplyDetail(negotiationId: string) {
  const queryClient = useQueryClient();
  return useCallback(
    (detail: NegotiationDetail) => {
      queryClient.setQueryData(queryKeys.negotiation(negotiationId), detail);
      void queryClient.invalidateQueries({ queryKey: ['negotiations'] });
      if (detail.search_session_id !== null) {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.selection(detail.search_session_id),
        });
        void queryClient.invalidateQueries({
          queryKey: queryKeys.searchSession(detail.search_session_id),
        });
      }
    },
    [queryClient, negotiationId],
  );
}

export type NegotiationPatchAction = 'pause' | 'resume' | 'stop';

export function usePatchNegotiation(negotiationId: string) {
  const apply = useApplyDetail(negotiationId);

  return useMutation({
    mutationFn: (action: NegotiationPatchAction) =>
      apiFetch<NegotiationDetail>(`/negotiations/${negotiationId}`, {
        method: 'PATCH',
        body: { action },
      }),
    onSuccess: apply,
  });
}

/**
 * Отклонить просьбу автохауса и продолжить переписку (§22.5).
 *
 * Одно нажатие с экрана решения: автохаусу уходит короткое письмо без причин
 * и оценок, переписка возвращается в работу. Необратимо — без оптимистичного
 * UI (CLAUDE.md §8).
 */
export function useDeclineRequest(negotiationId: string) {
  const apply = useApplyDetail(negotiationId);

  return useMutation({
    mutationFn: () =>
      apiFetch<NegotiationDetail>(`/negotiations/${negotiationId}/decline-request`, {
        method: 'POST',
        idempotencyKey: randomUuid(),
      }),
    onSuccess: apply,
  });
}

/**
 * Составить письмо агента заново после провала проверки (BE-602).
 *
 * Переписка возвращается к агенту, письмо пишет та же задача с теми же
 * проверками. Вариант приходит в `awaiting_actions` только пока не исчерпан
 * лимит автономных ответов; иначе сервер отвечает `409 DRAFT_RETRY_NOT_ALLOWED`.
 */
export function useRetryDraft(negotiationId: string) {
  const apply = useApplyDetail(negotiationId);

  return useMutation({
    mutationFn: () =>
      apiFetch<NegotiationDetail>(`/negotiations/${negotiationId}/redraft`, {
        method: 'POST',
        idempotencyKey: randomUuid(),
      }),
    onSuccess: apply,
  });
}

/**
 * Ответить автохаусу своими словами (§22.5).
 *
 * Текст проходит те же guardrails, что и письмо агента: провал приходит
 * `409` с кодами правил, и текст правит человек — обхода проверки нет.
 */
export function useUserReply(negotiationId: string) {
  const apply = useApplyDetail(negotiationId);

  return useMutation({
    mutationFn: (text: string) =>
      apiFetch<NegotiationDetail>(`/negotiations/${negotiationId}/reply`, {
        method: 'POST',
        idempotencyKey: randomUuid(),
        body: { text },
      }),
    onSuccess: apply,
  });
}

/**
 * Новая версия мандата (§21.3).
 *
 * Правка мандата — **новая версия**, а не изменение прежней: письма ушли по
 * старым границам, и переписать их задним числом нельзя.
 */
export function useCreateMandate(negotiationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (mandate: MandateInput) =>
      apiFetch(`/negotiations/${negotiationId}/mandate`, {
        method: 'POST',
        idempotencyKey: randomUuid(),
        body: mandate,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.negotiation(negotiationId) });
    },
  });
}

export interface AcceptOfferInput {
  offerId: string;
  /**
   * Необязательный телефон для связи с автохаусом (§39.4). Пустая строка и
   * `undefined` — одно и то же: телефона нет, передача идёт по почте.
   */
  contactPhone?: string | undefined;
}

export function useAcceptOffer(negotiationId: string) {
  const apply = useApplyDetail(negotiationId);

  return useMutation({
    mutationFn: ({ offerId, contactPhone }: AcceptOfferInput) =>
      apiFetch<NegotiationDetail>(`/negotiations/${negotiationId}/offers/${offerId}/accept`, {
        method: 'POST',
        idempotencyKey: randomUuid(),
        body: contactPhone?.trim() ? { contact_phone: contactPhone.trim() } : {},
      }),
    onSuccess: apply,
  });
}

export interface DeclineOfferInput {
  offerId: string;
  /** `false` — торг продолжается с новым мандатом (§22.7). */
  closeThread: boolean;
}

export function useDeclineOffer(negotiationId: string) {
  const apply = useApplyDetail(negotiationId);

  return useMutation({
    mutationFn: ({ offerId, closeThread }: DeclineOfferInput) =>
      apiFetch<NegotiationDetail>(`/negotiations/${negotiationId}/offers/${offerId}/decline`, {
        method: 'POST',
        idempotencyKey: randomUuid(),
        body: { close_thread: closeThread },
      }),
    onSuccess: apply,
  });
}

export type MessageAction = 'approve' | 'cancel' | 'send-now';

/**
 * Действия над запланированным письмом (§23.7).
 *
 * Отмена возможна только до перехода в `sending`: после письмо уже у
 * почтового сервера, и делать вид, что оно отозвано, нельзя. Ответ у этих
 * действий пустой, поэтому карточка перезапрашивается.
 */
export function useMessageAction(negotiationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ messageId, action }: { messageId: string; action: MessageAction }) =>
      apiFetch<null>(`/negotiations/${negotiationId}/messages/${messageId}/${action}`, {
        method: 'POST',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.negotiation(negotiationId) });
    },
  });
}
